/**
 * Validation helpers shared by the Worker and the client.
 *
 * Keeping them in one module means the client cannot accept input the Worker
 * will reject, and the Worker cannot rely on the client having checked it.
 */

import { LIMITS } from './constants'

/**
 * Pragmatic email check: one `@`, a non-empty local part, a dotted domain and
 * no whitespace. Full RFC 5322 validation is deliberately avoided because it
 * accepts addresses no mail provider will deliver to.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/

export function isValidEmail(value: string): boolean {
  return value.length <= LIMITS.emailMaxLength && EMAIL_PATTERN.test(value)
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

/** Six digit numeric verification code. */
const CODE_PATTERN = /^\d{6}$/

export function isValidVerificationCode(value: string): boolean {
  return CODE_PATTERN.test(value.trim())
}

export function trimmedLength(value: string): number {
  return value.trim().length
}

/** Local date-time without time zone, e.g. `2026-10-01T14:00`. */
const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
/** Local time of day, e.g. `14:30`. */
const LOCAL_TIME_PATTERN = /^\d{2}:\d{2}$/
/** Calendar date, e.g. `2026-10-01`. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function isValidLocalDateTime(value: string): boolean {
  if (!LOCAL_DATE_TIME_PATTERN.test(value)) {
    return false
  }
  return isValidDateOnly(value.slice(0, 10)) && isValidLocalTime(value.slice(11))
}

export function isValidLocalTime(value: string): boolean {
  if (!LOCAL_TIME_PATTERN.test(value)) {
    return false
  }
  const hours = Number(value.slice(0, 2))
  const minutes = Number(value.slice(3, 5))
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59
}

export function isValidDateOnly(value: string): boolean {
  if (!DATE_ONLY_PATTERN.test(value)) {
    return false
  }
  const [year, month, day] = value.split('-').map(Number) as [number, number, number]
  if (month < 1 || month > 12 || day < 1) {
    return false
  }
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return day <= daysInMonth
}

/**
 * Coordinates are always GCJ-02 (the datum AMap returns and expects). Values
 * outside the mainland China bounding box are rejected early: storing a WGS-84
 * coordinate by accident shifts every travel time by hundreds of metres.
 */
const GCJ02_BOUNDS = {
  minLat: 3.3,
  maxLat: 53.6,
  minLng: 73.4,
  maxLng: 135.1,
} as const

export function isGcj02Coordinate(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false
  }
  return (
    lat >= GCJ02_BOUNDS.minLat &&
    lat <= GCJ02_BOUNDS.maxLat &&
    lng >= GCJ02_BOUNDS.minLng &&
    lng <= GCJ02_BOUNDS.maxLng
  )
}

export function clampNumber(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min
  }
  return Math.min(Math.max(value, min), max)
}

/** Parses a numeric query/body value, returning `null` when unusable. */
export function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function parseInteger(value: unknown): number | null {
  const parsed = parseFiniteNumber(value)
  if (parsed === null || !Number.isInteger(parsed)) {
    return null
  }
  return parsed
}

/**
 * Parses a positive integer route segment, for example a recommendation round.
 *
 * A segment that is not one cannot address anything, so callers render their own
 * not-found state instead of issuing a request that is guaranteed to 404.
 */
export function parsePositiveInteger(value: string | undefined): number | undefined {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

/** Collapses whitespace and enforces a maximum length. */
export function normalizeSingleLine(value: string, maxLength: number): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength)
}

/** Normalises multi-line text while preserving intentional line breaks. */
export function normalizeMultiline(value: string, maxLength: number): string {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, maxLength)
}
